import clsx from 'clsx';
import React from 'react';
import { Box, Container, Typography, Grid, Link } from '@mui/material';
import LazyLoad from 'react-lazyload';
import { useStyles } from './styles';
import Faq from '../Faq';
import GraphicalThreeSteps from 'components/GraphicalThreeSteps';
import CarouselGraphicalAbstracts from 'components/CarouselGraphicalAbstracts';
import ResearchersAcademics from 'components/ResearchersAcademics';
import InfodesignGraphicalAbstract from 'components/InfodesignGraphicalAbstract';
import { getEnv, scrollToElement } from 'utils';
import PublishedSection from 'components/PublishedSection';

export default function MainContent() {
  const classes = useStyles();

  function scrollActionClick() {
    const headerElement = document.getElementById('header');

    scrollToElement(headerElement);
  }

  return (
    <>
      <GraphicalThreeSteps showButton={false} />

      <LazyLoad offset={100}>
        <CarouselGraphicalAbstracts />
      </LazyLoad>

      <LazyLoad offset={100}>
        <ResearchersAcademics />
      </LazyLoad>

      <LazyLoad offset={100}>
        <Box px={6} py={6} bgcolor="#FFFFFF">
          <Grid container={true} spacing={4} alignItems="center">
            <Grid item={true} xs={12} md={6}>
              <Box px={6}>
                <img
                  src="/images/createGraphicalAbstract/templates-icon.webp"
                  loading="lazy"
                  alt="Templates Icon"
                />

                <Typography className={classes.primaryColor} component="p" gutterBottom={true}>
                  <b>Visualize your research or paper in an engaging way</b>
                </Typography>

                <Typography>
                  Scientific illustrations are a part of an effective graphical abstract. With Mind
                  the Graph you can access 200+ templates for anatomy, biology, ecology, and 80+
                  more fields. Let attractive graphical abstracts talk to your readers.
                </Typography>
              </Box>
            </Grid>

            <Grid item={true} xs={12} md={6}>
              <Link onClick={scrollActionClick} underline="hover">
                <img
                  className={classes.sectionImage}
                  src="/images/createGraphicalAbstract/templates-group.webp"
                  loading="lazy"
                  alt="Templates Group image"
                />
              </Link>
            </Grid>
          </Grid>
        </Box>

        <Box px={6} py={6} bgcolor="#F1F1F1">
          <Grid container={true} spacing={2} alignItems="center">
            <Grid item={true} xs={12} md={6}>
              <Link onClick={scrollActionClick} underline="hover">
                <img
                  className={classes.sectionImage}
                  src="/images/createGraphicalAbstract/graphical-abstract-maker.webp"
                  loading="lazy"
                  alt="Graphical Abstract Maker image"
                />
              </Link>
            </Grid>

            <Grid item={true} xs={12} md={6}>
              <Box px={6}>
                <img
                  src="/images/createGraphicalAbstract/illustration-icon.webp"
                  loading="lazy"
                  alt="Illustration Icon"
                />

                <Typography className={classes.primaryColor} component="p" gutterBottom={true}>
                  <b>Create graphical abstracts for every journal with no effort</b>
                </Typography>

                <Typography>
                  Graphical abstract guidelines differ between publishers like Cell, Nature
                  Springer, Frontiers, Science and others, but with Mind the Graph it's not a
                  problem. You can easily change illustrations, fonts, image size, and file type.
                  Create and publish your research everywhere.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box px={6} py={6} bgcolor="#FFFFFF">
          <Grid container={true} spacing={2} alignItems="center">
            <Grid item={true} xs={12} md={6}>
              <Box px={6}>
                <img
                  src="/images/createGraphicalAbstract/eye-catching-icon.svg"
                  loading="lazy"
                  alt="Eye catching icon"
                />

                <Typography className={classes.primaryColor} component="p" gutterBottom={true}>
                  <b>Eye-catching data visualizations</b>
                </Typography>

                <Typography>
                  Build a graphical abstract that perfectly represents your findings. Mind the Graph
                  offers an unlimited amount of scientifically accurate illustrations, icons, and
                  figures, as well as charts for data visualization and a vast library of molecules
                  for chemistry and biochemistry!
                </Typography>
              </Box>
            </Grid>
            <Grid item={true} xs={12} md={6}>
              <Link onClick={scrollActionClick} underline="hover">
                <img
                  className={classes.sectionImage}
                  src="/images/createGraphicalAbstract/medical-illustrations.webp"
                  loading="lazy"
                  alt="Medical Illustrations image"
                />
              </Link>
            </Grid>
          </Grid>
        </Box>

        <Box px={6} py={6} bgcolor="#F1F1F1">
          <Grid container={true} spacing={2} alignItems="center">
            <Grid item={true} xs={12} md={6}>
              <Link onClick={scrollActionClick} underline="hover">
                <img
                  className={classes.sectionImage}
                  src="/images/createGraphicalAbstract/graphical-abstract-maker-2.webp"
                  loading="lazy"
                  alt="Graphical Abstract Maker 2 image"
                />
              </Link>
            </Grid>

            <Grid item={true} xs={12} md={6}>
              <Box px={6}>
                <img
                  src="/images/createGraphicalAbstract/graphical-maker-icon.svg"
                  loading="lazy"
                  alt="Graphical Maker icon"
                />

                <Typography className={classes.primaryColor} component="p" gutterBottom={true}>
                  <b>Hassle-free graphical abstract maker</b>
                </Typography>

                <Typography>
                  No need to have the best design skills to create an ideal graphical abstract. With
                  our easy-to-learn tools you can effortlessly customize and manage templates,{' '}
                  illustrations, and text for any scientific publication.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </LazyLoad>

      <LazyLoad offset={100}>
        <InfodesignGraphicalAbstract />
      </LazyLoad>

      <LazyLoad offset={100}>
        <Box px={6} py={6} bgcolor="#F1F1F1">
          <Grid container={true} spacing={2} alignItems="center">
            <Grid item={true} xs={12} md={6}>
              <Box px={6}>
                <Typography variant="h2" className={classes.title}>
                  What exactly is a <br /> graphical abstract?
                </Typography>

                <Typography>
                  In the world of science one of the most important things is the ability to show
                  our findings in a simple and understandable way. To reach this goal - a graphical{' '}
                  abstract comes into play. It is a single-panel image that gives readers an{' '}
                  immediate understanding of the main idea in the scientific paper.{' '}
                </Typography>

                <Box mt={4}>
                  <Typography>
                    By using visual materials to support minimal amounts of text we are able to{' '}
                    introduce readers to our research in a matter of seconds. In other words, a
                    graphical abstract is literally a figure that explains{' '}
                    <a href={`${getEnv('MTG_URL_APP')}/blog/visual-abstract/`}>
                      scientific information in a visual form
                    </a>
                    . It is worth pointing out that graphical abstracts are quickly rising in
                    popularity among many top publishers like Cell Press and many others.
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item={true} xs={12} md={6}>
              <Link onClick={scrollActionClick} underline="hover">
                <img
                  className={clsx(classes.sectionImage, classes.sectionImageGraphicalAbstract)}
                  src="/images/createGraphicalAbstract/what-is-graphical-abstract.webp"
                  loading="lazy"
                  alt="What is graphical abstract"
                />
              </Link>
            </Grid>
          </Grid>
        </Box>
      </LazyLoad>

      <LazyLoad offset={100}>
        <Box py={6}>
          <Container>
            <Faq />
          </Container>
        </Box>
      </LazyLoad>

      <LazyLoad offset={100}>
        <PublishedSection />
      </LazyLoad>
    </>
  );
}
